import { useReactiveVar } from "@apollo/client";
import { useRef, useMemo } from "react";
import { PACKAGE_ASPECT_RATIO, PC_MIN_ITEM_WIDTH, SP_MIN_ITEM_WIDTH } from "../../const";
import { useBuildVodDetailHref } from "~/components/blocks/product/hooks/use-build-vod-detail-href";
import { modalReactiveWidthVar } from "~/components/stores/detail-product/store";
import { RESTRICT_IMAGES } from "~/const/restriction-image";
import { useImageSrcFormat } from "~/hooks/use-image-src-format";
import { useMatchMedia } from "~/hooks/use-match-media";
import { useRatingBlock } from "~/hooks/use-rating-block";
import type { G_GetVideoQuery, G_RelatedBook, G_VideoRating } from "~/lib/graphql";
import { sendTrackingSelectItem } from "~/lib/tracking/events";

type UseCollectionItemArgs = {
  videos: {
    seasonId: string;
    video: {
      id: string;
      titleName: string;
      packageImage: string;
      isNewArrival: boolean;
      isExclusive: boolean;
      rating: Pick<G_VideoRating, "category">;
      customTag?: G_GetVideoQuery["video"]["customTag"];
    };
  }[];
  books: Pick<G_RelatedBook, "title" | "url" | "seriesId" | "thumbnail">[];
};

export const useCollectionItem = (args: UseCollectionItemArgs) => {
  const { videos, books } = args;
  const { buildVodDetailHref } = useBuildVodDetailHref();
  const { imageSrcFormat } = useImageSrcFormat({ aspectRatio: "PORTRAIT" });
  const { isPcSize } = useMatchMedia();

  const sectionRef = useRef<HTMLDivElement>(null);
  const modalReactiveWidth = useReactiveVar(modalReactiveWidthVar);
  const minWidth = isPcSize ? PC_MIN_ITEM_WIDTH : SP_MIN_ITEM_WIDTH;
  const displayItemCount = Math.floor(modalReactiveWidth / minWidth);

  const { isRestrictedContentByUserStatus } = useRatingBlock();

  const relatedVideoItems = useMemo(() => {
    return (
      videos.map(({ seasonId, video }, idx) => {
        const { id, titleName, packageImage, isNewArrival, isExclusive, rating, customTag } = video;
        const { href } = buildVodDetailHref({ category: rating.category, id });

        return {
          key: `CollectionVideos-${seasonId}`,
          alt: titleName,
          borderRadius: "8px",
          aspectRatio: PACKAGE_ASPECT_RATIO,
          isNewArrival,
          isExclusive,
          customTag,
          href,
          ...imageSrcFormat({
            imgSrc: isRestrictedContentByUserStatus(video.rating.category) ? RESTRICT_IMAGES.PACKAGE : packageImage,
          }),
          onClick: () => {
            sendTrackingSelectItem({
              seasonId: id,
              index: idx + 1,
              itemName: titleName,
              itemListName: "related_videos",
              itemListId: null,
              viaViewRank: idx + 1,
              viaShowPosition: 1,
            });
          },
        };
      }) ?? []
    );
  }, [videos]);

  const relatedBooksItems = useMemo(() => {
    return (
      books.map(({ seriesId, title, thumbnail, url }, idx) => {
        return {
          key: `CollectionBooks-${seriesId}`,
          alt: title,
          ...imageSrcFormat({ imgSrc: thumbnail }),
          borderRadius: "8px",
          aspectRatio: PACKAGE_ASPECT_RATIO,
          onClick: () => {
            sendTrackingSelectItem({
              seasonId: seriesId,
              itemName: title,
              index: idx + 1,
              itemListName: "related_books",
              itemListId: null,

              viaViewRank: idx + 1,
              viaShowPosition: 1,
            });
            window.open(url, "_blank", "noreferrer");
          },
        };
      }) ?? []
    );
  }, [books]);

  return {
    sectionRef,
    displayItemCount,
    relatedVideoItems,
    relatedBooksItems,
  };
};
